import { formatCount, normalizeDigits, toPersianDigits } from '@saay/formatting';

import type {
  PhysicalCardCityCode,
  PhysicalCardNumberingPolicyVersion,
  PhysicalCardNumberingPolicyVersionCreate,
  PhysicalCardNumberingValues,
  PhysicalCardProvinceCode,
} from '../api.js';
import { NUMBERING_POLICY_TYPE } from '../permissions.js';

/**
 * The numbering configuration as data (D-117, AC §31.6), with no React. `createPolicyVersion` takes
 * the whole configuration each time, so every row action of «کارت‌های فیزیکی» is one small change
 * applied to the version in force: `save(setCityCap(id, 60))` builds the full body from it. The
 * suggestions (next free province digits, next free city code) and the change line of «سابقه» live
 * here too.
 */

export type NumberingValues = PhysicalCardNumberingValues;
export type NumberingChange = (values: NumberingValues) => NumberingValues;

/** The technical bounds of §31.6. */
export const NUMBERING_BOUNDS = {
  cap: 10_000_000,
  cities: 1_000,
  provinces: 100,
  qrLinkBase: 200,
  series: 20,
} as const;

export function valuesOf(version: PhysicalCardNumberingPolicyVersion): NumberingValues {
  return {
    activeSeriesPrefix: version.activeSeriesPrefix,
    cities: version.cities,
    provinces: version.provinces,
    // A response always has it (AC §33.4); the generated type cannot say so, so missing reads null.
    qrLinkBase: version.qrLinkBase ?? null,
    series: version.series,
  };
}

/** The request body: the whole configuration, no `effectiveFrom` (it starts at once). */
export function toNumberingVersionCreate(
  values: NumberingValues,
  expectedVersion: number,
  reason: string,
): PhysicalCardNumberingPolicyVersionCreate {
  return {
    activeSeriesPrefix: values.activeSeriesPrefix,
    cities: values.cities.map((row) => ({
      cityCode: row.cityCode,
      cityId: row.cityId,
      issuanceCap: row.issuanceCap,
    })),
    expectedVersion,
    policyType: NUMBERING_POLICY_TYPE,
    provinces: values.provinces.map((row) => ({
      cityCodeWidth: row.cityCodeWidth,
      countryDigit: row.countryDigit,
      provinceDigit: row.provinceDigit,
      provinceId: row.provinceId,
    })),
    // A version is the whole configuration: leaving this out would clear the link (AC §33.4).
    qrLinkBase: values.qrLinkBase ?? null,
    reason: reason.trim(),
    series: values.series.map((row) => ({ prefix: row.prefix })),
  };
}

// ---------------------------------------------------------------------------------------------
// One change per row action

export function setCityCap(cityId: string, issuanceCap: number): NumberingChange {
  return (values) => ({
    ...values,
    cities: values.cities.map((row) => (row.cityId === cityId ? { ...row, issuanceCap } : row)),
  });
}

/** Adds a city, or replaces the one with the same id (its code and cap). */
export function putCity(city: PhysicalCardCityCode): NumberingChange {
  return (values) => ({
    ...values,
    cities: values.cities.some((row) => row.cityId === city.cityId)
      ? values.cities.map((row) => (row.cityId === city.cityId ? city : row))
      : [...values.cities, city],
  });
}

export function dropCity(cityId: string): NumberingChange {
  return (values) => ({
    ...values,
    cities: values.cities.filter((row) => row.cityId !== cityId),
  });
}

/** Adds a province, or replaces the one with the same id. */
export function putProvince(province: PhysicalCardProvinceCode): NumberingChange {
  return (values) => ({
    ...values,
    provinces: values.provinces.some((row) => row.provinceId === province.provinceId)
      ? values.provinces.map((row) => (row.provinceId === province.provinceId ? province : row))
      : [...values.provinces, province],
  });
}

/** Drops a province and the given cities of it (a city needs its province in the version). */
export function dropProvince(provinceId: string, cityIds: readonly string[]): NumberingChange {
  const drop = new Set(cityIds);
  return (values) => ({
    ...values,
    cities: values.cities.filter((row) => !drop.has(row.cityId)),
    provinces: values.provinces.filter((row) => row.provinceId !== provinceId),
  });
}

/** Composes changes left to right (a new province and its first city in one version). */
export function both(...changes: readonly NumberingChange[]): NumberingChange {
  return (values) => changes.reduce((next, change) => change(next), values);
}

export function addSeries(prefix: string, activate: boolean): NumberingChange {
  return (values) => ({
    ...values,
    activeSeriesPrefix: activate ? prefix : values.activeSeriesPrefix,
    series: [...values.series, { prefix }],
  });
}

export function activateSeries(prefix: string): NumberingChange {
  return (values) => ({ ...values, activeSeriesPrefix: prefix });
}

export function dropSeries(prefix: string): NumberingChange {
  return (values) => ({
    ...values,
    activeSeriesPrefix: values.activeSeriesPrefix === prefix ? null : values.activeSeriesPrefix,
    series: values.series.filter((row) => row.prefix !== prefix),
  });
}

export function setQrLinkBase(qrLinkBase: string | null): NumberingChange {
  return (values) => ({ ...values, qrLinkBase });
}

// ---------------------------------------------------------------------------------------------
// Suggestions and checks

export type CodeWidth = 1 | 2;

/** The full tag: country digit, province digit, and the city code («۰۱» + «۷» = «۰۱۷»). */
export function cityTagOf(
  province: Pick<PhysicalCardProvinceCode, 'countryDigit' | 'provinceDigit'>,
  cityCode: string,
): string {
  return `${province.countryDigit}${province.provinceDigit}${cityCode}`;
}

/**
 * The next free (country, province) pair: the one after the highest pair in use, else the first
 * hole; `(0, 1)` when no province has a code; `undefined` when all 100 are taken.
 */
export function nextProvinceDigits(
  provinces: readonly Pick<PhysicalCardProvinceCode, 'countryDigit' | 'provinceDigit'>[],
): { readonly countryDigit: number; readonly provinceDigit: number } | undefined {
  const used = new Set(provinces.map((row) => row.countryDigit * 10 + row.provinceDigit));
  const highest = used.size === 0 ? 0 : Math.max(...used);
  const order = [
    ...Array.from({ length: 99 - highest }, (_, index) => highest + 1 + index),
    ...Array.from({ length: highest + 1 }, (_, index) => index),
  ];
  const free = order.find((pair) => !used.has(pair) && pair !== 0);
  const pair = free ?? (used.has(0) ? undefined : 0);
  return pair === undefined
    ? undefined
    : { countryDigit: Math.floor(pair / 10), provinceDigit: pair % 10 };
}

/** The candidate codes of a width, in the order they are suggested («۱…۹، ۰» or «۰۱…۹۹، ۰۰»). */
function codeCandidates(width: CodeWidth): string[] {
  const size = width === 1 ? 10 : 100;
  return [...Array.from({ length: size - 1 }, (_, index) => index + 1), 0].map((code) =>
    String(code).padStart(width, '0'),
  );
}

/** The next free city code of a province; `undefined` when every code of its width is used. */
export function nextCityCode(width: CodeWidth, used: ReadonlySet<string>): string | undefined {
  return codeCandidates(width).find((code) => !used.has(code));
}

/** The codes the cities of a province already use (`except` is the city being edited). */
export function usedCityCodes(
  values: NumberingValues,
  provinceId: string,
  provinceOf: (cityId: string) => string | undefined,
  except?: string,
): Set<string> {
  return new Set(
    values.cities
      .filter((row) => row.cityId !== except && provinceOf(row.cityId) === provinceId)
      .map((row) => row.cityCode),
  );
}

export const digitsOnly = (value: string): string => normalizeDigits(value).replace(/\D/g, '');

/** The message for a typed city code, or `undefined` when it fits the province (AC §31.6). */
export function cityCodeProblem(
  code: string,
  width: CodeWidth,
  used: ReadonlySet<string>,
): string | undefined {
  if (code.length !== width || !/^[0-9]+$/.test(code)) {
    return width === 1 ? 'یک رقم وارد کنید.' : 'دو رقم وارد کنید (مثل ۰۱).';
  }
  return used.has(code) ? 'این کد را شهر دیگری از همین استان دارد.' : undefined;
}

/** The message for a typed cap, or `undefined` for an integer 0 to 10,000,000. */
export function capProblem(raw: string): string | undefined {
  const cap = Number(raw);
  return raw === '' || !/^[0-9]+$/.test(raw) || !Number.isInteger(cap) || cap > NUMBERING_BOUNDS.cap
    ? 'عددی از ۰ تا ۱۰٬۰۰۰٬۰۰۰ وارد کنید.'
    : undefined;
}

/** The message for a typed series prefix, or `undefined`. */
export function prefixProblem(prefix: string, values: NumberingValues): string | undefined {
  if (!/^[0-9]{6}$/.test(prefix)) return 'شش رقم وارد کنید.';
  if (values.series.some((row) => row.prefix === prefix)) return 'این سری از قبل هست.';
  return undefined;
}

/** The `qrLinkBase` expression of AC §33.4. */
const QR_LINK_BASE_PATTERN =
  /^https:\/\/[A-Za-z0-9]([A-Za-z0-9-]*[A-Za-z0-9])?(\.[A-Za-z0-9]([A-Za-z0-9-]*[A-Za-z0-9])?)*(:[0-9]{1,5})?(\/[A-Za-z0-9._~%-]+)*$/;

/** The message for a typed link base, or `undefined`; an empty field means the default link. */
export function qrLinkBaseProblem(base: string): string | undefined {
  if (base === '') return undefined;
  return base.length > NUMBERING_BOUNDS.qrLinkBase || !QR_LINK_BASE_PATTERN.test(base)
    ? 'آدرس باید با https:// شروع شود و آخرش / نداشته باشد. علامت ؟ و # هم مجاز نیست.'
    : undefined;
}

/** The one-line warning of a cap under the live count (allowed: it only stops issuance). */
export function capBelowLiveWarning(cap: number, liveCount: number): string | undefined {
  return cap < liveCount
    ? `کمتر از ${formatCount(liveCount)} کارت فعال است؛ تا کارت‌های فعال کمتر نشود، کارت جدیدی صادر نمی‌شود.`
    : undefined;
}

// ---------------------------------------------------------------------------------------------
// Reasons: each action starts with a sensible chip, so one tap plus «ذخیره» is enough.

export const REASON_PRESETS = {
  activateSeries: ['تغییر سری فعال', 'سری قبلی تمام شد'],
  addCity: ['شهر جدید', 'درخواست خریداران'],
  addProvince: ['استان جدید', 'گسترش به استان جدید'],
  addSeries: ['سری جدید از صادرکننده', 'سری قبلی تمام شد'],
  capDown: ['کاهش سقف', 'توقف صدور در شهر'],
  capUp: ['افزایش سقف', 'درخواست خریداران'],
  dropCity: ['حذف شهر بدون کارت', 'تعریف اشتباه'],
  dropProvince: ['حذف استان بدون کارت', 'تعریف اشتباه'],
  dropSeries: ['حذف سری بدون کارت', 'تعریف اشتباه'],
  editCity: ['تغییر سقف', 'اصلاح کد شهر'],
  editProvince: ['اصلاح کد استان'],
  editQrLink: ['تغییر آدرس لینک QR'],
} as const satisfies Readonly<Record<string, readonly string[]>>;

export type ReasonKind = keyof typeof REASON_PRESETS;

export function defaultReason(kind: ReasonKind): string {
  return REASON_PRESETS[kind][0];
}

// ---------------------------------------------------------------------------------------------
// «سابقه»: what a version changed, in plain words

export interface NameLookup {
  readonly city: (cityId: string) => string | undefined;
  readonly province: (provinceId: string) => string | undefined;
}

/** The changes from `previous` to `next`, one short phrase each (none for the first version). */
export function describeChanges(
  previous: NumberingValues | undefined,
  next: NumberingValues,
  names: NameLookup,
): string[] {
  if (previous === undefined) return [];
  const city = (id: string) => names.city(id) ?? 'یک شهر';
  const province = (id: string) => names.province(id) ?? 'یک استان';
  const digits = (value: string) => toPersianDigits(value);
  const lines: string[] = [];
  for (const row of next.series) {
    if (!previous.series.some((item) => item.prefix === row.prefix)) {
      lines.push(`سری ${digits(row.prefix)} اضافه شد`);
    }
  }
  for (const row of previous.series) {
    if (!next.series.some((item) => item.prefix === row.prefix)) {
      lines.push(`سری ${digits(row.prefix)} حذف شد`);
    }
  }
  if (previous.activeSeriesPrefix !== next.activeSeriesPrefix) {
    lines.push(
      next.activeSeriesPrefix === null
        ? 'سری فعال برداشته شد'
        : `سری فعال: ${digits(next.activeSeriesPrefix)}`,
    );
  }
  for (const row of next.provinces) {
    const before = previous.provinces.find((item) => item.provinceId === row.provinceId);
    if (before === undefined) lines.push(`استان ${province(row.provinceId)} اضافه شد`);
    else if (
      before.countryDigit !== row.countryDigit ||
      before.provinceDigit !== row.provinceDigit ||
      before.cityCodeWidth !== row.cityCodeWidth
    ) {
      lines.push(`کد استان ${province(row.provinceId)} عوض شد`);
    }
  }
  for (const row of previous.provinces) {
    if (!next.provinces.some((item) => item.provinceId === row.provinceId)) {
      lines.push(`استان ${province(row.provinceId)} حذف شد`);
    }
  }
  const added = next.cities.filter(
    (row) => !previous.cities.some((item) => item.cityId === row.cityId),
  );
  if (added.length > 3) lines.push(`${formatCount(added.length)} شهر اضافه شد`);
  else for (const row of added) lines.push(`شهر ${city(row.cityId)} اضافه شد`);
  for (const row of next.cities) {
    const before = previous.cities.find((item) => item.cityId === row.cityId);
    if (before === undefined) continue;
    if (before.cityCode !== row.cityCode) lines.push(`کد ${city(row.cityId)} عوض شد`);
    if (before.issuanceCap !== row.issuanceCap) {
      lines.push(
        `سقف ${city(row.cityId)} از ${formatCount(before.issuanceCap)} به ${formatCount(row.issuanceCap)}`,
      );
    }
  }
  const removed = previous.cities.filter(
    (row) => !next.cities.some((item) => item.cityId === row.cityId),
  );
  if (removed.length > 3) lines.push(`${formatCount(removed.length)} شهر حذف شد`);
  else for (const row of removed) lines.push(`شهر ${city(row.cityId)} حذف شد`);
  if ((previous.qrLinkBase ?? null) !== (next.qrLinkBase ?? null)) {
    lines.push(
      (next.qrLinkBase ?? null) === null ? 'لینک QR به آدرس پیش‌فرض برگشت' : 'آدرس لینک QR عوض شد',
    );
  }
  return lines;
}

/** At most three phrases, then «و N تغییر دیگر». */
export function changeLine(changes: readonly string[]): string {
  if (changes.length === 0) return '';
  const shown = changes.slice(0, 3).join('، ');
  const more = changes.length - 3;
  return more > 0 ? `${shown} و ${formatCount(more)} تغییر دیگر` : shown;
}

// ---------------------------------------------------------------------------------------------
// Deep links from «خلاصه» (one-time: the page opens the action, then drops `edit` from the URL)

export const CITY_PARAM = 'city';
export const EDIT_PARAM = 'edit';

/** `cap` opens a city's inline cap editor, `code` the add-city dialog, `series` the add-series
 * dialog (to be activated), and `activate` the activate-series dialog. */
export type EditTarget = 'activate' | 'cap' | 'code' | 'series';

export function editTargetOf(value: string | null): EditTarget | undefined {
  return value === 'activate' || value === 'cap' || value === 'code' || value === 'series'
    ? value
    : undefined;
}

/** A city's state for the «شهرها» filter: the K9 flag, or `NONE` for a city without a code. */
export type CityState = 'AT_CAP' | 'NEAR_CAP' | 'OK' | 'NONE';
